import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Badge, Box, ClickAwayListener, Divider, Paper, Popper, useMediaQuery, Tooltip, Button } from '@mui/material';
import MainCard from 'components/ui/Card/MainCard';
import IconButton from 'components/ui/Button/IconButton';
import Transitions from 'components/common/Transitions/Transitions';
import { ThemeMode } from 'types/config';
import { Typography } from '@mui/material';
import {
    useGetApiRequestsQuery,
    usePutApiRequestsByRequestIdAcceptMutation,
    usePutApiRequestsByRequestIdRejectMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import { permissions } from 'constants/permissions';
import useCheckPermission from 'hooks/useCheckPermission';
import { throwSuccessToast } from 'utils';
import { successMessages } from 'constants/messages';
import { Stack } from '@mui/material';
import { IconKey } from 'utils/icons';

const AccessRequestNotifications = () => {
    const [accept] = usePutApiRequestsByRequestIdAcceptMutation();
    const [reject] = usePutApiRequestsByRequestIdRejectMutation();
    const { data: accessRequests, isLoading } = useGetApiRequestsQuery({}, { pollingInterval: 60000 });
    const perms = useCheckPermission();
    const allowedAccessRequests =
        Array.isArray(accessRequests) &&
        accessRequests?.filter(
            request =>
                perms(permissions.prjUserAssignment, request.ProjectShortName) &&
                request.State !== 4 &&
                request.State !== 5 &&
                request.Type === 1
        );

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const anchorRef = useRef<any>(null);
    const [active, setActive] = useState(Array.isArray(allowedAccessRequests) && allowedAccessRequests?.length);
    const [open, setOpen] = useState(false);
    const handleToggle = () => {
        setOpen(prevOpen => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    useEffect(() => {
        setActive(Array.isArray(allowedAccessRequests) ? allowedAccessRequests?.length : 0);
    }, [allowedAccessRequests]);

    const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';
    const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'grey.100';

    const onAccept = async (requestId: string | null | undefined) => {
        if (requestId) {
            await accept({ requestId: requestId }).unwrap();

            throwSuccessToast({ message: successMessages.accepted });
        }
    };

    const onReject = async (requestId: string | null | undefined) => {
        if (requestId) {
            await reject({ requestId: requestId }).unwrap();

            throwSuccessToast({ message: successMessages.rejected });
        }
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 0.75 }} id='access-request-notifications'>
            <Tooltip title='Project Access Requests'>
                <Box>
                    <IconButton
                        color='secondary'
                        variant='light'
                        sx={{
                            color: 'text.primary',
                            bgcolor: open ? iconBackColorOpen : iconBackColor,
                            borderRadius: '20px',
                        }}
                        aria-label='open profile'
                        ref={anchorRef}
                        aria-controls={open ? 'profile-grow' : undefined}
                        aria-haspopup='true'
                        disabled={isLoading}
                        onClick={handleToggle}>
                        <Badge badgeContent={active} color='primary'>
                            <IconKey size={20} />
                        </Badge>
                    </IconButton>
                </Box>
            </Tooltip>
            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? -5 : 0, 9],
                            },
                        },
                    ],
                }}>
                {({ TransitionProps }) => (
                    <Transitions
                        type='grow'
                        position={matchesXs ? 'top' : 'top-right'}
                        sx={{ overflow: 'hidden' }}
                        in={open}
                        {...TransitionProps}>
                        <Paper
                            sx={{
                                boxShadow: theme.customShadows.z1,
                                width: 420,
                                borderRadius: 2,
                                overflow: 'hidden',
                                [theme.breakpoints.down('md')]: {
                                    width: 320,
                                },
                            }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard
                                    title='Access Requests'
                                    elevation={0}
                                    border={false}
                                    content={false}
                                    sx={{
                                        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'grey.900' : 'grey.50',
                                    }}>
                                    {Array.isArray(allowedAccessRequests) && allowedAccessRequests?.length > 0 ? (
                                        <Box
                                            sx={{
                                                maxHeight: 400,
                                                overflow: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    width: '8px',
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    bgcolor: 'transparent',
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    bgcolor:
                                                        theme.palette.mode === ThemeMode.DARK ? 'grey.700' : 'grey.300',
                                                    borderRadius: '4px',
                                                    '&:hover': {
                                                        bgcolor:
                                                            theme.palette.mode === ThemeMode.DARK
                                                                ? 'grey.600'
                                                                : 'grey.400',
                                                    },
                                                },
                                            }}>
                                            {allowedAccessRequests?.map((request, index) => (
                                                <Box key={request.RequestId}>
                                                    <Box
                                                        sx={{
                                                            p: 2,
                                                            mx: 1,
                                                            my: 0.5,
                                                            borderRadius: 1.5,
                                                            bgcolor: 'background.paper',
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                bgcolor: theme.palette.action.hover,
                                                                borderColor: theme.palette.primary.main,
                                                                boxShadow: theme.customShadows.z1,
                                                            },
                                                        }}>
                                                        <Stack spacing={1.5}>
                                                            <Box>
                                                                <Typography
                                                                    variant='caption'
                                                                    color='text.secondary'
                                                                    sx={{ display: 'block', mb: 0.5 }}>
                                                                    User
                                                                </Typography>
                                                                <Typography variant='subtitle2' fontWeight={600}>
                                                                    {request.Email}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography
                                                                    variant='caption'
                                                                    color='text.secondary'
                                                                    sx={{ display: 'block', mb: 0.5 }}>
                                                                    Project
                                                                </Typography>
                                                                <Typography variant='subtitle2' fontWeight={600}>
                                                                    {request.ProjectShortName}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ pt: 1 }}>
                                                                <Stack direction='row' spacing={1}>
                                                                    <Button
                                                                        fullWidth
                                                                        variant='contained'
                                                                        onClick={() => onAccept(request.RequestId)}
                                                                        sx={{
                                                                            borderRadius: 1,
                                                                            textTransform: 'none',
                                                                            fontWeight: 600,
                                                                            fontSize: '0.875rem',
                                                                            transition: 'all 0.2s ease',
                                                                            '&:hover': {
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: theme.customShadows.z1,
                                                                            },
                                                                        }}>
                                                                        Accept
                                                                    </Button>
                                                                    <Button
                                                                        fullWidth
                                                                        variant='contained'
                                                                        color='error'
                                                                        onClick={() => onReject(request.RequestId)}
                                                                        sx={{
                                                                            borderRadius: 1,
                                                                            textTransform: 'none',
                                                                            fontWeight: 600,
                                                                            fontSize: '0.875rem',
                                                                            transition: 'all 0.2s ease',
                                                                            '&:hover': {
                                                                                transform: 'translateY(-2px)',
                                                                                boxShadow: theme.customShadows.z1,
                                                                            },
                                                                        }}>
                                                                        Reject
                                                                    </Button>
                                                                </Stack>
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                    {index < allowedAccessRequests.length - 1 && (
                                                        <Divider sx={{ my: 0.5 }} />
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                textAlign: 'center',
                                                py: 6,
                                                px: 3,
                                            }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                No Access Requests
                                            </Typography>
                                        </Box>
                                    )}
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </Box>
    );
};

export default AccessRequestNotifications;
